/**
 * PullToRefresh - Mobile-style pull to refresh component
 *
 * Features:
 * - Touch gesture support
 * - Animated spinner
 * - Haptic feedback
 * - Threshold-based activation
 */

import React, { useState, useCallback, useRef } from 'react';
import { motion, useAnimation, PanInfo } from 'framer-motion';
import { useFeedback } from '../../contexts/FeedbackContext';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  className?: string;
  threshold?: number;
  disabled?: boolean;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  children,
  className = '',
  threshold = 80,
  disabled = false,
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const controls = useAnimation();
  const { triggerHaptic, playSound } = useFeedback();

  const isAtTop = useCallback(() => {
    if (!containerRef.current) return true;
    return containerRef.current.scrollTop <= 0;
  }, []);

  const handlePanStart = useCallback((event: MouseEvent | TouchEvent | PointerEvent) => {
    if (disabled || isRefreshing) return;
    if (!isAtTop()) return;

    const clientY = 'touches' in event
      ? (event as TouchEvent).touches[0].clientY
      : (event as MouseEvent).clientY;

    startYRef.current = clientY;
    setIsPulling(true);
  }, [disabled, isRefreshing, isAtTop]);

  const handlePan = useCallback((event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (disabled || isRefreshing || !isPulling) return;
    if (!isAtTop()) {
      setPullDistance(0);
      return;
    }

    const delta = Math.max(0, info.offset.y);
    // Apply resistance factor for natural feel
    const resistance = 0.5;
    const distance = Math.pow(delta, resistance) * 2;

    setPullDistance(Math.min(distance, threshold * 2));

    // Haptic feedback at threshold
    if (distance >= threshold && pullDistance < threshold) {
      triggerHaptic('medium');
    }
  }, [disabled, isRefreshing, isPulling, isAtTop, threshold, pullDistance, triggerHaptic]);

  const handlePanEnd = useCallback(async () => {
    if (!isPulling) return;

    setIsPulling(false);

    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      triggerHaptic('success');

      // Keep the spinner visible
      setPullDistance(60);

      try {
        await onRefresh();
        playSound('refresh');
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      // Animate back to top
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, threshold, isRefreshing, onRefresh, triggerHaptic, playSound]);

  // Calculate spinner properties
  const progress = Math.min(pullDistance / threshold, 1);
  const rotation = pullDistance * 2;

  return (
    <motion.div
      ref={containerRef}
      className={`relative overflow-y-auto ${className}`}
      onPanStart={handlePanStart}
      onPan={handlePan}
      onPanEnd={handlePanEnd}
    >
      {/* Pull indicator */}
      <motion.div
        className="absolute left-0 right-0 flex justify-center pointer-events-none z-10"
        style={{ top: -40 }}
        animate={{
          y: pullDistance,
          opacity: Math.min(progress, 1),
        }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      >
        <motion.div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            progress >= 1 ? 'bg-green-500' : 'bg-gray-700'
          } transition-colors`}
          animate={{
            scale: isRefreshing ? [1, 1.1, 1] : 1,
            rotate: isRefreshing ? 360 : rotation,
          }}
          transition={{
            rotate: isRefreshing
              ? { duration: 1, repeat: Infinity, ease: 'linear' }
              : { duration: 0 },
            scale: isRefreshing
              ? { duration: 0.5, repeat: Infinity }
              : {},
          }}
        >
          {isRefreshing ? (
            <svg
              className="w-5 h-5 text-white animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                style={{
                  transform: `rotate(${progress >= 1 ? 180 : 0}deg)`,
                  transformOrigin: 'center',
                  transition: 'transform 0.2s',
                }}
              />
            </svg>
          )}
        </motion.div>
      </motion.div>

      {/* Content */}
      <motion.div
        animate={{ y: pullDistance > 0 ? pullDistance : 0 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
};

export default PullToRefresh;
